import type { TypedDocumentNode } from "@apollo/client";

import {
  buildBrowseVariables,
  buildLoadMoreVariables,
  buildSearchLoadMoreVariables,
  buildSearchVariables,
  type LoadMoreVariables,
  type SearchGroup,
} from "@intelligems/ig-clients/storefront-navigation/navigation-variables";
import type {
  NavigationGroup,
  NavigationResponse,
  NavigationSearchResponse,
} from "@intelligems/ig-clients/storefront-navigation/navigation-items";
import type { PreviewLocalization } from "@intelligems/ig-clients/storefront-navigation/preview-localization";

import type {
  StorefrontClient,
  StorefrontCredential,
} from "@src/features/experienceBuilder/toolbar/Navigator/storefront/StorefrontClient";
import {
  FetchStorefrontNavigationArticlesDocument,
  FetchStorefrontNavigationCollectionsDocument,
  FetchStorefrontNavigationDocument,
  FetchStorefrontNavigationPagesDocument,
  FetchStorefrontNavigationProductsDocument,
  FetchStorefrontNavigationSearchCollectionsDocument,
  FetchStorefrontNavigationSearchDocument,
  FetchStorefrontNavigationSearchGroupDocument,
} from "@src/generated-storefront";

/**
 * The typed Navigator queries. Each one sends the variables that the shared
 * module builds from the Preview localization, so every query has
 * `@inContext`.
 */

/** The store, credential and Preview localization of one Navigator query. */
export interface NavigationQueryTarget {
  shop: string;
  credential: StorefrontCredential;
  localization: PreviewLocalization;
}

/** The first page of every group, with no query. */
export const fetchBrowsePage = (
  client: StorefrontClient,
  { shop, credential, localization }: NavigationQueryTarget
) =>
  client.query({
    shop,
    credential,
    document: FetchStorefrontNavigationDocument,
    variables: buildBrowseVariables(localization),
  });

/**
 * The "load more" document of each group, with no query. Each document
 * resolves to the group's connection under the group's own key.
 */
const BROWSE_LOAD_MORE_DOCUMENTS: {
  [TGroup in NavigationGroup]: TypedDocumentNode<
    Record<TGroup, NavigationResponse[TGroup]>,
    LoadMoreVariables
  >;
} = {
  pages: FetchStorefrontNavigationPagesDocument,
  collections: FetchStorefrontNavigationCollectionsDocument,
  products: FetchStorefrontNavigationProductsDocument,
  articles: FetchStorefrontNavigationArticlesDocument,
};

/**
 * The next page of one group, with no query: the group's own document,
 * after the group's cursor. Resolves to that group's connection.
 */
const fetchBrowseLoadMorePage = async <TGroup extends NavigationGroup>(
  client: StorefrontClient,
  { shop, credential, localization }: NavigationQueryTarget,
  group: TGroup,
  after: string
): Promise<NavigationResponse[TGroup]> =>
  (
    await client.query({
      shop,
      credential,
      document: BROWSE_LOAD_MORE_DOCUMENTS[group],
      variables: buildLoadMoreVariables(localization, after),
    })
  )[group];

/**
 * The first page of a typed search. The shared module reads the input: plain
 * text searches every group, and a `<prefix>/<term>` input searches one group.
 */
export const fetchSearchPage = async (
  client: StorefrontClient,
  { shop, credential, localization }: NavigationQueryTarget,
  input: string
): Promise<NavigationSearchResponse> => {
  const search = buildSearchVariables(localization, input);
  switch (search.kind) {
    case "allGroups":
      return client.query({
        shop,
        credential,
        document: FetchStorefrontNavigationSearchDocument,
        variables: search.variables,
      });
    case "group": {
      const response: NavigationSearchResponse = {};
      response[search.group] = (
        await client.query({
          shop,
          credential,
          document: FetchStorefrontNavigationSearchGroupDocument,
          variables: search.variables,
        })
      ).search;
      return response;
    }
    case "collections":
      return client.query({
        shop,
        credential,
        document: FetchStorefrontNavigationSearchCollectionsDocument,
        variables: search.variables,
      });
  }
};

/**
 * The next page of one group of a typed search, after the group's cursor.
 * Resolves to that group's `search` connection.
 */
const fetchSearchLoadMorePage = async (
  client: StorefrontClient,
  { shop, credential, localization }: NavigationQueryTarget,
  input: string,
  group: SearchGroup,
  after: string
) =>
  (
    await client.query({
      shop,
      credential,
      document: FetchStorefrontNavigationSearchGroupDocument,
      variables: buildSearchLoadMoreVariables(
        localization,
        input,
        group,
        after
      ),
    })
  ).search;

/**
 * The next page of one group: of the browse list when there is no query, or
 * of the typed search. Collections in a typed search come from
 * `predictiveSearch`, which has no "load more".
 */
export const fetchLoadMorePage = async (
  client: StorefrontClient,
  target: NavigationQueryTarget,
  query: string | undefined,
  group: NavigationGroup,
  after: string
): Promise<NavigationResponse[NavigationGroup]> => {
  if (query === undefined) {
    return fetchBrowseLoadMorePage(client, target, group, after);
  }
  if (group === "collections") {
    throw new Error("A typed search in collections has no load more");
  }
  return fetchSearchLoadMorePage(client, target, query, group, after);
};
